import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, Search, User, Mail, Calendar as CalendarIcon,
  DollarSign, MessageSquare, Settings, ChevronLeft, ChevronRight, X, Plus,
  Trash2, Tag, GripVertical, Rss, Copy, ExternalLink, Share2, Zap, UserCircle,
  Archive, ArchiveRestore, AlertTriangle, Paperclip, ArrowRightLeft, CheckSquare, Check,
} from "lucide-react";
import { ShareModal, AutomationsModal } from "@/components/admin/BoardExtras";
import MediaUploader from "@/components/media/MediaUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const KIND_ICON = { booking: DollarSign, guest: MessageSquare, lead: User, client: UserCircle };
const KIND_LABEL = { booking: "Booking", guest: "Spotlight", lead: "Lead", client: "Client" };
const KIND_ACCENT = { booking: "#00f0ff", guest: "#a78bfa", lead: "#ff0055", client: "#22c55e" };

const fmtShort = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return ""; }
};

// ---------------- Archive modal ----------------
const ArchiveModal = ({ slug, board, onClose, onChange }) => {
  const [rows, setRows] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // {kind,id,label}
  const [busy, setBusy] = useState(false);

  // Which kinds belong to THIS board — cap the archive view to what's relevant
  const boardKinds = useMemo(() => {
    if (!board?.sources) return null;
    const mapping = { bookings: "booking", guest_requests: "guest", leads: "lead", invoices: "invoice", clients: "client", episodes: "episode" };
    return board.sources.map((s) => mapping[s.collection]).filter(Boolean);
  }, [board]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/cards/archived`, { headers: authHeaders() });
      const filtered = {};
      let count = 0;
      Object.entries(data.archived || {}).forEach(([k, v]) => {
        if (boardKinds && !boardKinds.includes(k)) return;
        if (v && v.length) { filtered[k] = v; count += v.length; }
      });
      setRows(filtered);
      setTotal(count);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load archive");
    } finally {
      setLoading(false);
    }
  }, [boardKinds]);

  useEffect(() => { load(); }, [load]);

  const restore = async (kind, doc) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/cards/${kind}/${doc.id}/restore`, {}, { headers: authHeaders() });
      toast.success("Restored to the board.");
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  const hardDelete = async () => {
    if (!confirmDelete) return;
    const { kind, id } = confirmDelete;
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/cards/${kind}/${id}`, { headers: authHeaders() });
      toast.success(
        `Deleted forever. Save this undo token if you change your mind: ${data.undo_token?.slice(0, 8)}…`,
        { duration: 8000 },
      );
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const titleOf = (d) => d.title || d.brief_project || d.name || d.headline || d.email || d.id?.slice(0, 8) || "Untitled";

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm p-4 md:p-8 overflow-y-auto" onClick={onClose} data-testid="archive-modal">
      <div className="max-w-3xl mx-auto rounded-3xl bg-[#0a0510] border border-white/10 p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
              <Archive size={20} className="text-[#ff0055]" /> Archive
            </h2>
            <p className="text-[#a395c3] text-xs mt-1">
              {total} archived card{total === 1 ? "" : "s"} · restore anytime, or delete forever
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        {loading && (
          <div className="text-white/60 py-8 text-center"><Loader2 size={16} className="animate-spin inline mr-2" /> Loading archive…</div>
        )}

        {!loading && total === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-white/[0.03] border border-white/10 flex items-center justify-center mx-auto mb-4">
              <Archive size={22} className="text-white/40" />
            </div>
            <p className="text-white/60 text-sm">The archive is empty. Trash any card to send it here.</p>
          </div>
        )}

        {!loading && total > 0 && (
          <div className="space-y-6">
            {Object.entries(rows).map(([kind, docs]) => (
              <div key={kind}>
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/50 mb-2" style={{ color: KIND_ACCENT[kind] }}>
                  {KIND_LABEL[kind] || kind} · {docs.length}
                </div>
                <div className="space-y-2">
                  {docs.map((d) => (
                    <div key={d.id} data-testid={`archived-row-${kind}-${d.id}`} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/10">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{titleOf(d)}</div>
                        <div className="text-white/40 text-[11px] mt-0.5 truncate">
                          Archived {fmtShort(d.archived_at)}{d.archived_by ? ` · by ${d.archived_by}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => restore(kind, d)}
                          disabled={busy}
                          data-testid={`restore-${kind}-${d.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/30 hover:bg-[#22c55e]/20 text-[#22c55e] text-xs font-semibold disabled:opacity-50"
                        >
                          <ArchiveRestore size={11} /> Restore
                        </button>
                        <button
                          onClick={() => setConfirmDelete({ kind, id: d.id, label: titleOf(d) })}
                          disabled={busy}
                          data-testid={`hard-delete-${kind}-${d.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ff0055]/10 border border-[#ff0055]/30 hover:bg-[#ff0055]/20 text-[#ff0055] text-xs font-semibold disabled:opacity-50"
                        >
                          <Trash2 size={11} /> Delete forever
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="max-w-md w-full rounded-3xl bg-[#0a0510] border border-[#ff0055]/40 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#ff0055]/15 text-[#ff0055] flex items-center justify-center flex-shrink-0"><AlertTriangle size={18} /></div>
              <div>
                <h3 className="font-display font-black text-lg tracking-tight">Delete forever?</h3>
                <p className="text-[#a395c3] text-sm mt-1">
                  This removes <span className="text-white">{confirmDelete.label}</span> from Mongo. We&apos;ll issue a 30-day undo token you can save — but the card disappears from the archive immediately.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-xs">Cancel</button>
              <button
                onClick={hardDelete}
                disabled={busy}
                data-testid="confirm-hard-delete"
                className="px-4 py-2 rounded-full bg-[#ff0055] text-white text-xs font-semibold hover:bg-[#ff0055]/80 disabled:opacity-50"
              >
                {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
                Yes, delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



// -------------------------------- Label pill ---------------------------------
const LABEL_COLORS = ["#00f0ff", "#a78bfa", "#ff0055", "#22c55e", "#f59e0b", "#f97316", "#ec4899", "#3b82f6"];
const hashColor = (s) => LABEL_COLORS[Math.abs(s.split("").reduce((a, c) => a + c.charCodeAt(0), 0)) % LABEL_COLORS.length];

const LabelPill = ({ text }) => (
  <span
    className="inline-block text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border font-semibold"
    style={{ color: hashColor(text), borderColor: `${hashColor(text)}55`, background: `${hashColor(text)}12` }}
  >
    {text}
  </span>
);

// -------------------------------- Card tile ---------------------------------
// Explicit approve/decline actions for cards sitting in a pending-style column.
// The keys are `${card.kind}:${card.column}` — hitting a button calls onMove.
const PENDING_ACTIONS = {
  "client:pending_approval": [
    { label: "Approve", target: "onboarding", color: "#22c55e" },
    { label: "Decline", target: "declined", color: "#ef4444" },
  ],
  "guest:pending": [
    { label: "Approve", target: "approved", color: "#22c55e" },
    { label: "Decline", target: "declined", color: "#ef4444" },
  ],
  "guest:new": [
    { label: "Approve", target: "approved", color: "#22c55e" },
    { label: "Decline", target: "declined", color: "#ef4444" },
  ],
  "booking:pending_review": [
    { label: "Confirm", target: "booked", color: "#22c55e" },
    { label: "Decline", target: "declined", color: "#ef4444" },
  ],
  "booking:new": [
    { label: "Confirm", target: "booked", color: "#22c55e" },
    { label: "Decline", target: "declined", color: "#ef4444" },
  ],
};

const CardTile = ({ card, onDragStart, onOpen, onArchive, onMove, calendar = false, columns = [], selected = false, selectMode = false }) => {
  const Icon = KIND_ICON[card.kind] || User;
  const accent = KIND_ACCENT[card.kind] || "#00f0ff";
  const [confirming, setConfirming] = useState(false);
  const [movePickerOpen, setMovePickerOpen] = useState(false);

  const handleArchiveClick = (e) => {
    e.stopPropagation();
    if (!confirming) {
      setConfirming(true);
      // Auto-dismiss the confirm state after 3s so a stray tap doesn't linger
      setTimeout(() => setConfirming(false), 3000);
      return;
    }
    setConfirming(false);
    onArchive && onArchive(card);
  };

  const otherColumns = (columns || []).filter((c) => c.id !== card.column);

  return (
    <div
      draggable={!selectMode && (card.kind !== "lead" || calendar === false)}
      onDragStart={(e) => onDragStart(e, card)}
      onClick={() => onOpen(card)}
      data-testid={`card-${card.kind}-${card.id}`}
      className={`group relative rounded-xl border bg-[#0a0510] cursor-grab active:cursor-grabbing transition-all ${
        selected
          ? "border-[#00f0ff] ring-2 ring-[#00f0ff]/40 bg-[#00f0ff]/[0.06]"
          : "border-white/10 hover:border-white/25 hover:bg-white/[0.04]"
      } ${calendar ? "p-2" : "p-4"}`}
      style={calendar ? { borderLeft: `3px solid ${accent}` } : undefined}
    >
      {selectMode && !calendar && (
        <div
          className={`absolute top-2 left-2 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
            selected ? "bg-[#00f0ff] border-[#00f0ff]" : "border-white/30 bg-black/30"
          }`}
          data-testid={`card-select-${card.kind}-${card.id}`}
          aria-hidden
        >
          {selected && <Check size={11} className="text-[#05050a]" strokeWidth={3} />}
        </div>
      )}
      {!calendar && (
        <div className="flex items-start justify-between gap-2 mb-2">
          <div
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.24em] font-semibold border flex-shrink-0"
            style={{ color: accent, borderColor: `${accent}40`, background: `${accent}10` }}
          >
            <Icon size={9} /> {KIND_LABEL[card.kind]}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-[10px] text-white/40 font-mono-tight">{fmtShort(card.created_at)}</div>
            {onMove && otherColumns.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setMovePickerOpen((v) => !v); }}
                  data-testid={`card-move-${card.kind}-${card.id}`}
                  aria-label="Move card to another column"
                  aria-expanded={movePickerOpen}
                  title="Move to…"
                  className="w-7 h-7 md:w-6 md:h-6 rounded-md flex items-center justify-center transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 touch-manipulation bg-white/[0.08] text-white/70 hover:bg-[#00f0ff]/20 hover:text-[#00f0ff] active:bg-[#00f0ff]/30"
                >
                  <ArrowRightLeft size={12} />
                </button>
                {movePickerOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[70]"
                      onClick={(e) => { e.stopPropagation(); setMovePickerOpen(false); }}
                    />
                    <div
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`card-move-menu-${card.kind}-${card.id}`}
                      className="absolute right-0 top-full mt-1 z-[75] w-[220px] max-h-[280px] overflow-y-auto rounded-xl border border-white/15 bg-[#0b0715]/98 backdrop-blur-md shadow-2xl shadow-black/60 p-1.5"
                    >
                      <div className="text-[9px] uppercase tracking-[0.24em] text-white/40 px-2 py-1.5">
                        Move to
                      </div>
                      {otherColumns.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMovePickerOpen(false);
                            onMove(card, c.id);
                          }}
                          data-testid={`card-move-to-${c.id}-${card.kind}-${card.id}`}
                          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-[12px] hover:bg-white/[0.06] transition-colors"
                        >
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: c.color || "#00f0ff" }}
                          />
                          <span className="truncate">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {onArchive && (
              <button
                type="button"
                onClick={handleArchiveClick}
                onMouseLeave={() => setConfirming(false)}
                data-testid={`card-archive-${card.kind}-${card.id}`}
                aria-label={confirming ? "Confirm archive" : "Archive card"}
                title={confirming ? "Tap again to archive" : "Archive"}
                className={`w-7 h-7 md:w-6 md:h-6 rounded-md flex items-center justify-center transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 touch-manipulation ${
                  confirming
                    ? "bg-[#ff0055] text-white opacity-100 md:opacity-100 ring-2 ring-[#ff0055]/40"
                    : "bg-white/[0.08] text-white/70 hover:bg-[#ff0055]/20 hover:text-[#ff0055] active:bg-[#ff0055]/30"
                }`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        </div>
      )}
      <div className={`font-semibold ${calendar ? "text-[11px] leading-tight" : "text-sm leading-snug mb-1"} line-clamp-2`}>
        {card.title}
      </div>
      {!calendar && card.subtitle && (
        <div className="text-[#a395c3] text-xs line-clamp-1">{card.subtitle}</div>
      )}
      {!calendar && card.email && (
        <div className="text-white/40 text-[11px] mt-2 truncate flex items-center gap-1.5">
          <Mail size={10} /> {card.email}
        </div>
      )}
      {!calendar && card.labels && card.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2.5">
          {card.labels.map((l) => <LabelPill key={l} text={l} />)}
        </div>
      )}
      {!calendar && onMove && PENDING_ACTIONS[`${card.kind}:${card.column}`] && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-white/[0.06]">
          {PENDING_ACTIONS[`${card.kind}:${card.column}`].map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={(e) => { e.stopPropagation(); onMove(card, a.target); }}
              data-testid={`card-action-${a.label.toLowerCase()}-${card.kind}-${card.id}`}
              className="flex-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition-all"
              style={{ color: a.color, borderColor: `${a.color}55`, background: `${a.color}12` }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// -------------------------------- Card detail (with label editor) ---------------------------------
const CardDetail = ({ card, onClose, onLabelsChange }) => {
  const [labels, setLabels] = useState(card?.labels || []);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  if (!card) return null;
  const Icon = KIND_ICON[card.kind] || User;
  const accent = KIND_ACCENT[card.kind] || "#00f0ff";

  const addLabel = async () => {
    const v = draft.trim();
    if (!v || labels.includes(v) || labels.length >= 8) return;
    const next = [...labels, v];
    setLabels(next); setDraft("");
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cards/${card.kind}/${card.id}/labels`, { labels: next }, { headers: authHeaders() });
      onLabelsChange(card, next);
    } catch { toast.error("Label save failed"); setLabels(labels); }
    finally { setSaving(false); }
  };
  const removeLabel = async (l) => {
    const next = labels.filter((x) => x !== l);
    setLabels(next); setSaving(true);
    try {
      await axios.put(`${API}/admin/cards/${card.kind}/${card.id}/labels`, { labels: next }, { headers: authHeaders() });
      onLabelsChange(card, next);
    } catch { toast.error("Label save failed"); setLabels(labels); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="card-detail-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg rounded-2xl bg-[#11091a] border border-white/15 p-6 md:p-8 max-h-[85vh] overflow-auto" data-testid="card-detail">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center text-white/70" aria-label="Close" data-testid="card-detail-close">×</button>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border mb-4" style={{ color: accent, borderColor: `${accent}40`, background: `${accent}10` }}>
          <Icon size={11} /> {KIND_LABEL[card.kind]}
        </div>
        <div className="font-display font-bold text-2xl mb-2 leading-tight">{card.title}</div>
        {card.subtitle && <div className="text-[#a395c3] text-sm mb-5">{card.subtitle}</div>}

        <div className="space-y-3 text-sm">
          {card.email && <DetailRow icon={Mail} label="Email" value={card.email} />}
          {card.phone && <DetailRow icon={User} label="Phone" value={card.phone} />}
          {card.preferred_date && <DetailRow icon={CalendarIcon} label="Preferred" value={`${card.preferred_date}${card.preferred_time ? ` @ ${card.preferred_time}` : ""}`} />}
          {card.budget > 0 && <DetailRow icon={DollarSign} label="Budget" value={`$${card.budget}`} />}
        </div>

        {/* Labels editor */}
        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-2.5 flex items-center gap-2">
            <Tag size={11} /> Labels
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="card-labels-list">
            {labels.map((l) => (
              <span key={l} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full border font-semibold" style={{ color: hashColor(l), borderColor: `${hashColor(l)}55`, background: `${hashColor(l)}12` }}>
                {l}
                <button onClick={() => removeLabel(l)} className="ml-0.5 hover:opacity-70" data-testid={`remove-label-${l}`}>×</button>
              </span>
            ))}
            {labels.length === 0 && <span className="text-white/40 text-xs">No labels yet.</span>}
          </div>
          <div className="flex gap-2">
            <input
              type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLabel()}
              placeholder="Add label (Enter)…"
              data-testid="add-label-input"
              className="flex-1 px-3 py-2 rounded-full bg-white/[0.04] border border-white/15 text-xs focus:outline-none focus:border-[#00f0ff]/50"
            />
            <button onClick={addLabel} disabled={saving || !draft.trim()} data-testid="add-label-btn" className="px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-xs hover:bg-white/[0.12] disabled:opacity-50">
              Add
            </button>
          </div>
        </div>

        {card.brief && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-1.5">Brief</div>
            <div className="rounded-xl bg-black/40 border border-white/10 p-3 text-white/85 max-h-40 overflow-auto whitespace-pre-wrap text-sm">
              {card.brief}
            </div>
          </div>
        )}

        {/* Attachments — contracts, mood boards, deliverables, invoices */}
        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-2.5 flex items-center gap-2">
            <Paperclip size={11} /> Attachments
          </div>
          <MediaUploader
            ownerType="card"
            ownerId={`${card.kind}:${card.id}`}
            authMode="admin"
            compact
            title=""
            emptyLabel="No attachments — drop a contract, brief, or deliverable."
            testIdPrefix={`card-media-${card.kind}-${card.id}`}
          />
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-white/50 flex-shrink-0"><Icon size={13} /></div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">{label}</div>
      <div className="text-white/90 truncate">{value}</div>
    </div>
  </div>
);

// -------------------------------- iCal subscribe modal ---------------------------------
const SubscribeModal = ({ slug, onClose }) => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/boards/${slug}/ics/subscribe`, { headers: authHeaders() });
        setInfo(data);
      } catch { toast.error("Couldn't load subscribe link"); }
    })();
  }, [slug]);

  const copy = async (kind, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(null), 1400);
    } catch { toast.error("Couldn't copy — long-press the link instead."); }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="subscribe-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg rounded-2xl bg-[#11091a] border border-white/15 p-6 md:p-8" data-testid="subscribe-modal">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center" aria-label="Close">
          <X size={16} />
        </button>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border border-[#00f0ff]/40 bg-[#00f0ff]/10 text-[#00f0ff] mb-4">
          <Rss size={11} /> Live calendar feed
        </div>
        <div className="font-display font-bold text-2xl mb-2">Subscribe in your calendar app</div>
        <p className="text-[#a395c3] text-sm mb-6">
          Every card with a date shows up in Google/Apple Calendar automatically. Feed auto-refreshes every 15 min.
        </p>

        {!info ? (
          <div className="flex items-center gap-2 text-white/60 text-sm"><Loader2 size={14} className="animate-spin" /> Generating link…</div>
        ) : (
          <>
            <div className="space-y-4">
              <SubscribeRow
                label="iPhone · Apple Calendar" hint="Tap → Safari opens → confirm subscribe"
                value={info.webcal_url} onCopy={() => copy("apple", info.webcal_url)} copied={copied === "apple"}
                testId="subscribe-webcal"
                openLabel="Open in Calendar"
              />
              <SubscribeRow
                label="Google Calendar" hint="Other calendars → + → From URL → paste"
                value={info.https_url} onCopy={() => copy("google", info.https_url)} copied={copied === "google"}
                testId="subscribe-https"
              />
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/50 leading-relaxed">
              This link is unique to your admin account. Anyone with the URL can read the calendar (no PII beyond names + subjects). If it leaks, ping to rotate.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SubscribeRow = ({ label, hint, value, onCopy, copied, testId, openLabel }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
    <div className="flex items-start justify-between gap-3 mb-2">
      <div>
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-white/50 text-xs mt-0.5">{hint}</div>
      </div>
      <div className="flex items-center gap-2">
        {openLabel && (
          <a
            href={value}
            data-testid={`${testId}-open`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-xs hover:bg-white"
          >
            <ExternalLink size={12} /> {openLabel}
          </a>
        )}
        <button
          onClick={onCopy}
          data-testid={`${testId}-copy`}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-colors ${
            copied
              ? "bg-[#22c55e] text-[#05050a]"
              : "bg-white/[0.05] border border-white/15 text-white/85 hover:bg-white/[0.12]"
          }`}
        >
          <Copy size={12} /> {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
    <div className="rounded-md bg-black/40 border border-white/10 px-2.5 py-2 text-[11px] text-white/70 font-mono-tight break-all">
      {value}
    </div>
  </div>
);

// -------------------------------- Column editor ---------------------------------
const ColumnEditor = ({ board, onClose, onSaved }) => {
  const [cols, setCols] = useState(board.columns.map((c) => ({ ...c })));
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const update = (i, patch) => setCols((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const remove = (i) => setCols((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => setCols((prev) => [...prev, {
    id: `col-${Date.now()}`, label: "New column", color: "#a78bfa", order: prev.length,
  }]);
  const onDragStart = (i) => setDragIdx(i);
  const onDropAt = (i) => {
    if (dragIdx == null || dragIdx === i) return setDragIdx(null);
    const next = [...cols]; const [moved] = next.splice(dragIdx, 1); next.splice(i, 0, moved);
    setCols(next.map((c, idx) => ({ ...c, order: idx })));
    setDragIdx(null);
  };
  const save = async () => {
    setSaving(true);
    try {
      const cleaned = cols
        .filter((c) => c.label.trim())
        .map((c, i) => ({ ...c, order: i, label: c.label.trim() }));
      const { data } = await axios.patch(`${API}/admin/boards/${board.slug}`, { columns: cleaned }, { headers: authHeaders() });
      toast.success("Columns updated");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="column-editor-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md rounded-2xl bg-[#11091a] border border-white/15 p-6 md:p-8" data-testid="column-editor">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center" aria-label="Close">
          <X size={16} />
        </button>
        <div className="font-display font-bold text-2xl mb-1">Columns</div>
        <div className="text-[#a395c3] text-sm mb-6">Drag to reorder. Rename inline. Cards in removed columns snap to the first column.</div>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="column-list">
          {cols.map((c, i) => (
            <div
              key={c.id}
              draggable onDragStart={() => onDragStart(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropAt(i)}
              className={`flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/10 p-2 ${dragIdx === i ? "opacity-40" : ""}`}
              data-testid={`col-editor-row-${c.id}`}
            >
              <GripVertical size={14} className="text-white/40 cursor-grab flex-shrink-0" />
              <input
                type="color" value={c.color} onChange={(e) => update(i, { color: e.target.value })}
                className="w-7 h-7 rounded-md border border-white/15 bg-transparent cursor-pointer flex-shrink-0"
                data-testid={`col-color-${c.id}`}
              />
              <input
                type="text" value={c.label} onChange={(e) => update(i, { label: e.target.value })}
                className="flex-1 min-w-0 bg-transparent border-0 focus:outline-none text-sm"
                data-testid={`col-label-${c.id}`}
              />
              <button onClick={() => remove(i)} className="w-7 h-7 rounded-md bg-white/[0.05] hover:bg-[#ff0055]/15 hover:text-[#ff0055] text-white/50 flex items-center justify-center flex-shrink-0" data-testid={`col-remove-${c.id}`}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
        <button onClick={add} className="w-full mt-3 py-2.5 rounded-xl border border-dashed border-white/15 hover:border-[#00f0ff]/40 text-white/60 text-sm inline-flex items-center justify-center gap-2" data-testid="col-editor-add">
          <Plus size={14} /> Add column
        </button>
        <button onClick={save} disabled={saving} className="w-full mt-4 py-3 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="col-editor-save">
          {saving && <Loader2 size={14} className="animate-spin" />} Save columns
        </button>
      </div>
    </div>
  );
};

// -------------------------------- Kanban view ---------------------------------
const KanbanView = ({ board, dataRef, onMove, onOpen, onArchive, focusColumn }) => {
  const [dragging, setDragging] = useState(null);
  const [overCol, setOverCol] = useState(null);
  // Bulk-select mode — when on, tapping a card toggles selection instead
  // of opening it. Selected cards can then all be moved in one action
  // via the sticky bottom toolbar.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const columns = board.columns.slice().sort((a, b) => a.order - b.order);
  const cards = dataRef.cards || {};

  // Scroll the focused column into view when it changes (deep-link support).
  useEffect(() => {
    if (!focusColumn) return;
    const el = document.querySelector(`[data-testid="kanban-col-${focusColumn}"]`);
    if (el) {
      try {
        el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      } catch { /* older browsers */ }
    }
  }, [focusColumn, cards]);

  const toggleSelect = (card) => {
    const key = `${card.kind}:${card.id}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const cardIsSelected = (card) => selected.has(`${card.kind}:${card.id}`);

  const clearSelection = () => {
    setSelected(new Set());
    setSelectMode(false);
    setBulkPickerOpen(false);
  };

  // Flatten all cards so we can find the actual card objects by their keys
  // when the bulk move fires.
  const allCards = Object.values(cards).flat();

  const bulkMove = async (targetColId) => {
    setBulkBusy(true);
    setBulkPickerOpen(false);
    try {
      // Fire moves sequentially so the optimistic-update logic in onMove
      // doesn't clobber itself. Small N (< 50), so total latency is fine.
      const targets = allCards.filter(
        (c) => selected.has(`${c.kind}:${c.id}`) && c.column !== targetColId,
      );
      for (const card of targets) {
        // eslint-disable-next-line no-await-in-loop
        await onMove(card, targetColId);
      }
    } finally {
      setBulkBusy(false);
      clearSelection();
    }
  };

  return (
    <div>
      {/* Select-mode toggle bar */}
      <div className="flex items-center justify-between gap-3 mb-3 -mx-1">
        <div className="text-[11px] text-white/50">
          {selectMode ? `${selected.size} selected` : "Drag a card between columns, or use Select for bulk moves"}
        </div>
        {!selectMode ? (
          <button
            type="button"
            onClick={() => setSelectMode(true)}
            data-testid="kanban-select-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-xs hover:bg-white/[0.1]"
          >
            <CheckSquare size={12} /> Select
          </button>
        ) : (
          <button
            type="button"
            onClick={clearSelection}
            data-testid="kanban-select-cancel"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 text-xs hover:bg-white/[0.1]"
          >
            <X size={12} /> Cancel
          </button>
        )}
      </div>

      <div className="overflow-x-auto pb-8 -mx-5 md:-mx-10 px-5 md:px-10" data-testid="kanban-scroll">
        <div className="flex gap-4 min-w-max">
        {columns.map((col) => {
          const items = cards[col.id] || [];
          const isOver = overCol === col.id;
          const isFocused = focusColumn === col.id;
          return (
            <div
              key={col.id}
              onDragOver={(e) => { e.preventDefault(); if (overCol !== col.id) setOverCol(col.id); }}
              onDragLeave={() => setOverCol((v) => (v === col.id ? null : v))}
              onDrop={(e) => {
                e.preventDefault(); setOverCol(null);
                const card = dragging; setDragging(null);
                if (card && card.column !== col.id) onMove(card, col.id);
              }}
              data-testid={`kanban-col-${col.id}`}
              className={`w-[300px] flex-shrink-0 rounded-2xl border transition-colors ${
                isOver
                  ? "border-[#00f0ff]/60 bg-white/[0.05]"
                  : isFocused
                    ? "border-[#f59e0b]/70 bg-[#f59e0b]/[0.06] shadow-[0_0_0_2px_rgba(245,158,11,0.25)]"
                    : "border-white/10 bg-white/[0.02]"
              }`}
            >
              <div className="px-4 pt-4 pb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                  <div className="font-semibold text-sm">{col.label}</div>
                  {isFocused && (
                    <span className="text-[9px] uppercase tracking-[0.24em] font-bold text-[#f59e0b]" data-testid="col-focus-badge">
                      · focused
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/50 font-mono-tight">{items.length}</div>
              </div>
              <div className="px-3 pb-3 space-y-2 max-h-[70vh] overflow-y-auto">
                {items.length === 0 && <div className="text-[11px] text-white/30 py-6 text-center italic">Drop a card here</div>}
                {items.map((c) => (
                  <CardTile
                    key={`${c.kind}-${c.id}`}
                    card={c}
                    onDragStart={(e, c) => setDragging(c)}
                    onOpen={selectMode ? toggleSelect : onOpen}
                    onArchive={selectMode ? undefined : onArchive}
                    onMove={selectMode ? undefined : onMove}
                    columns={columns}
                    selected={cardIsSelected(c)}
                    selectMode={selectMode}
                  />
                ))}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Sticky bulk-move toolbar */}
      {selectMode && selected.size > 0 && (
        <div
          data-testid="kanban-bulk-toolbar"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] w-[calc(100%-2rem)] max-w-[520px] rounded-2xl border border-[#00f0ff]/40 bg-[#0b0715]/95 backdrop-blur-md shadow-2xl shadow-black/60 px-4 py-3 flex items-center gap-3"
        >
          <div className="text-sm font-semibold text-white flex-shrink-0">
            {selected.size} card{selected.size === 1 ? "" : "s"}
          </div>
          <div className="relative flex-1 flex justify-end">
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => setBulkPickerOpen((v) => !v)}
              data-testid="kanban-bulk-move-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00f0ff] text-[#05050a] text-xs font-bold hover:bg-white disabled:opacity-50"
            >
              {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRightLeft size={12} />}
              Move to…
            </button>
            {bulkPickerOpen && (
              <>
                <div
                  className="fixed inset-0 z-[80]"
                  onClick={() => setBulkPickerOpen(false)}
                />
                <div
                  onClick={(e) => e.stopPropagation()}
                  data-testid="kanban-bulk-move-menu"
                  className="absolute right-0 bottom-full mb-2 z-[85] w-[240px] max-h-[280px] overflow-y-auto rounded-xl border border-white/15 bg-[#0b0715]/98 backdrop-blur-md shadow-2xl shadow-black/60 p-1.5"
                >
                  <div className="text-[9px] uppercase tracking-[0.24em] text-white/40 px-2 py-1.5">
                    Move all to
                  </div>
                  {columns.map((col) => (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => bulkMove(col.id)}
                      data-testid={`kanban-bulk-move-to-${col.id}`}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left text-[12px] hover:bg-white/[0.06] transition-colors"
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: col.color || "#00f0ff" }}
                      />
                      <span className="truncate">{col.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------------- Calendar view ---------------------------------
const CalendarView = ({ board, dataRef, onReschedule, onOpen }) => {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [dragging, setDragging] = useState(null);

  const cardsByDate = dataRef.cards_by_date || {};
  const undated = dataRef.undated || [];

  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const firstWeekday = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
  const days = [];
  for (let i = 0; i < firstWeekday; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    days.push({ day: d, iso });
  }

  const nudge = (delta) => {
    const next = new Date(cursor); next.setMonth(cursor.getMonth() + delta); setCursor(next);
  };

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => nudge(-1)} data-testid="cal-prev" className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center"><ChevronLeft size={14} /></button>
          <div className="font-display font-bold text-xl w-48 text-center">{monthLabel}</div>
          <button onClick={() => nudge(1)} data-testid="cal-next" className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center"><ChevronRight size={14} /></button>
        </div>
        <button onClick={() => setCursor(() => { const d = new Date(); d.setDate(1); return d; })} data-testid="cal-today" className="px-4 py-2 text-xs rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12]">Today</button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.24em] text-white/40 mb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} className="rounded-lg bg-white/[0.01] border border-white/5 min-h-[110px]" />;
          const items = cardsByDate[d.iso] || [];
          return (
            <div
              key={d.iso}
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => {
                e.preventDefault();
                const card = dragging; setDragging(null);
                if (card && card.preferred_date !== d.iso) await onReschedule(card, d.iso);
              }}
              data-testid={`cal-day-${d.iso}`}
              className="rounded-lg bg-white/[0.02] border border-white/10 min-h-[110px] p-1.5 hover:bg-white/[0.04] transition-colors"
            >
              <div className="text-[11px] font-semibold text-white/60 mb-1 px-1">{d.day}</div>
              <div className="space-y-1">
                {items.slice(0, 3).map((c) => (
                  <div key={`${c.kind}-${c.id}`} draggable onDragStart={() => setDragging(c)}>
                    <CardTile card={c} onDragStart={(e, c) => setDragging(c)} onOpen={onOpen} calendar />
                  </div>
                ))}
                {items.length > 3 && (
                  <div className="text-[10px] text-white/40 px-1">+{items.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {undated.length > 0 && (
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-3">Unscheduled ({undated.length})</div>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="cal-undated">
            {undated.map((c) => (
              <div key={`${c.kind}-${c.id}`} draggable onDragStart={() => setDragging(c)}>
                <CardTile card={c} onDragStart={(e, c) => setDragging(c)} onOpen={onOpen} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------------- Page ---------------------------------
export default function AdminBoardDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const focusColumn = searchParams.get("column") || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState("");
  const [editingCols, setEditingCols] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  const onArchive = async (card) => {
    // optimistic remove from board
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (prev.cards) {
        next.cards = { ...prev.cards };
        Object.keys(next.cards).forEach((c) => {
          next.cards[c] = next.cards[c].filter((x) => !(x.kind === card.kind && x.id === card.id));
        });
        next.totals = Object.fromEntries(Object.entries(next.cards).map(([k, v]) => [k, v.length]));
      }
      if (prev.cards_by_date) {
        next.cards_by_date = { ...prev.cards_by_date };
        Object.keys(next.cards_by_date).forEach((d) => {
          next.cards_by_date[d] = (next.cards_by_date[d] || []).filter((x) => !(x.kind === card.kind && x.id === card.id));
          if (next.cards_by_date[d].length === 0) delete next.cards_by_date[d];
        });
        next.undated = (prev.undated || []).filter((x) => !(x.kind === card.kind && x.id === card.id));
      }
      return next;
    });
    try {
      await axios.post(
        `${API}/admin/cards/${card.kind}/${card.id}/archive`,
        {},
        { headers: authHeaders() },
      );
      toast.success("Archived — restore anytime from the archive view.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
      load();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/boards/${slug}/cards`, { headers: authHeaders() });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load board");
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const onMove = async (card, colId) => {
    // optimistic
    setData((prev) => {
      const next = { ...prev, cards: { ...prev.cards } };
      Object.keys(next.cards).forEach((c) => {
        next.cards[c] = next.cards[c].filter((x) => !(x.kind === card.kind && x.id === card.id));
      });
      next.cards[colId] = [{ ...card, column: colId }, ...(next.cards[colId] || [])];
      next.totals = Object.fromEntries(Object.entries(next.cards).map(([k, v]) => [k, v.length]));
      return next;
    });
    try {
      await axios.patch(`${API}/admin/boards/${slug}/cards/${card.kind}/${card.id}/move`, { column: colId }, { headers: authHeaders() });
      toast.success(`Moved to ${colId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
      load();
    }
  };

  const onReschedule = async (card, iso) => {
    setData((prev) => {
      if (!prev.cards_by_date) return prev;
      const next = { ...prev, cards_by_date: { ...prev.cards_by_date } };
      Object.keys(next.cards_by_date).forEach((d) => {
        next.cards_by_date[d] = (next.cards_by_date[d] || []).filter((x) => !(x.kind === card.kind && x.id === card.id));
        if (next.cards_by_date[d].length === 0) delete next.cards_by_date[d];
      });
      const moved = { ...card, preferred_date: iso };
      next.cards_by_date[iso] = [moved, ...(next.cards_by_date[iso] || [])];
      return next;
    });
    try {
      await axios.patch(`${API}/admin/boards/${slug}/cards/${card.kind}/${card.id}/reschedule`, { date: iso }, { headers: authHeaders() });
      toast.success(`Rescheduled to ${iso}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reschedule failed");
      load();
    }
  };

  const onLabelsChange = (card, labels) => {
    setData((prev) => {
      if (!prev) return prev;
      const patch = (arr) => arr.map((c) => (c.kind === card.kind && c.id === card.id ? { ...c, labels } : c));
      if (prev.cards) {
        const next = { ...prev, cards: { ...prev.cards } };
        Object.keys(next.cards).forEach((k) => next.cards[k] = patch(next.cards[k]));
        return next;
      }
      if (prev.cards_by_date) {
        const next = { ...prev, cards_by_date: { ...prev.cards_by_date } };
        Object.keys(next.cards_by_date).forEach((k) => next.cards_by_date[k] = patch(next.cards_by_date[k]));
        next.undated = patch(next.undated || []);
        return next;
      }
      return prev;
    });
  };

  const board = data?.board;

  // Filter by query
  const filteredData = useMemo(() => {
    if (!data || !query.trim()) return data;
    const q = query.toLowerCase();
    const match = (c) =>
      (c.title || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.labels || []).some((l) => l.toLowerCase().includes(q));
    if (data.cards) {
      const out = { ...data, cards: {} };
      Object.entries(data.cards).forEach(([k, v]) => (out.cards[k] = v.filter(match)));
      return out;
    }
    if (data.cards_by_date) {
      const out = { ...data, cards_by_date: {} };
      Object.entries(data.cards_by_date).forEach(([k, v]) => {
        const kept = v.filter(match); if (kept.length) out.cards_by_date[k] = kept;
      });
      out.undated = (data.undated || []).filter(match);
      return out;
    }
    return data;
  }, [data, query]);

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full blur-[120px]" style={{ background: `${board?.accent || "#00f0ff"}12` }} />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <Link to="/admin/boards" data-testid="board-back" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-3">
              <ArrowLeft size={14} /> All boards
            </Link>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
              {board?.name || "Loading…"}
              <span style={{ color: board?.accent }}>.</span>
            </h1>
            {board?.description && <p className="text-[#a395c3] text-sm mt-2">{board.description}</p>}
          </div>
          <div className="flex items-center gap-2 self-start lg:self-auto flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" data-testid="board-search" className="pl-9 pr-3 py-2 rounded-full bg-white/[0.04] border border-white/15 text-sm w-48 focus:outline-none focus:border-[#00f0ff]/50" />
            </div>
            {board?.view === "kanban" && (
              <button onClick={() => setEditingCols(true)} data-testid="edit-columns-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.1] text-xs">
                <Settings size={12} /> Columns
              </button>
            )}
            <button
              onClick={() => setShowSubscribe(true)}
              data-testid="subscribe-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-[#00f0ff]/15 hover:border-[#00f0ff]/40 hover:text-[#00f0ff] text-xs transition-colors"
              title="Subscribe in Google/Apple Calendar"
            >
              <Rss size={12} /> Subscribe
            </button>
            <button
              onClick={() => setShowShare(true)}
              data-testid="share-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-[#22c55e]/15 hover:border-[#22c55e]/40 hover:text-[#22c55e] text-xs transition-colors"
              title="Public read-only share link"
            >
              <Share2 size={12} /> Share
            </button>
            {board?.view === "kanban" && (
              <button
                onClick={() => setShowAutomations(true)}
                data-testid="automations-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-[#a78bfa]/15 hover:border-[#a78bfa]/40 hover:text-[#a78bfa] text-xs transition-colors"
                title="Automations · trigger on column entry"
              >
                <Zap size={12} /> Rules
              </button>
            )}
            <button
              onClick={() => setShowArchive(true)}
              data-testid="archive-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-[#ff0055]/15 hover:border-[#ff0055]/40 hover:text-[#ff0055] text-xs transition-colors"
              title="View archived cards & restore or delete forever"
            >
              <Archive size={12} /> Archive
            </button>
            <button onClick={load} disabled={loading} data-testid="board-refresh" className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] flex items-center justify-center disabled:opacity-50" aria-label="Refresh">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
          </div>
        </div>

        {loading && !data && <div className="text-white/60"><Loader2 size={16} className="animate-spin inline mr-2" /> Loading…</div>}
        {data && board?.view === "kanban" && (
          <KanbanView board={board} dataRef={filteredData} onMove={onMove} onOpen={setDetail} onArchive={onArchive} focusColumn={focusColumn} />
        )}
        {data && board?.view === "calendar" && (
          <CalendarView board={board} dataRef={filteredData} onReschedule={onReschedule} onOpen={setDetail} />
        )}
      </div>

      <CardDetail card={detail} onClose={() => setDetail(null)} onLabelsChange={onLabelsChange} />
      {editingCols && board && (
        <ColumnEditor board={board} onClose={() => setEditingCols(false)} onSaved={(updated) => { setEditingCols(false); load(); }} />
      )}
      {showSubscribe && <SubscribeModal slug={slug} onClose={() => setShowSubscribe(false)} />}
      {showShare && <ShareModal slug={slug} onClose={() => setShowShare(false)} />}
      {showAutomations && <AutomationsModal slug={slug} board={board} onClose={() => setShowAutomations(false)} />}
      {showArchive && (
        <ArchiveModal
          slug={slug}
          board={board}
          onClose={() => setShowArchive(false)}
          onChange={load}
        />
      )}
    </div>
  );
}
